import React from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { fmtCurrency, fmtHours } from '../../lib/display-labels';
import { shopGapMessage } from '../../constants/shop-pay';

/**
 * One clock-in shift as Generate priced it — `draft.shopEntries[]` on
 * POST /payroll/pay-periods/:id/generate (`ShopShiftPricing` in the API's
 * shop-pay.service.ts).
 *
 * The last four keys arrived with api#1097. A US hourly-basis worker is paid
 * for EVERY approved clock-in, field or shop, at their pay-profile base rate,
 * so this array now holds field shifts too, and these say which rate priced a
 * shift and where and for whom the hours were worked.
 */
export interface ClockPayEntry {
  timesheetId: string;
  userId: string;
  workerName: string;
  workDate: string;
  hours: number | null;
  hourlyRate: number | null;
  overtimeEligible: boolean | null;
  /** hours × rate from the API, or null with a `gap`. Never a stand-in 0. */
  amount: number | null;
  gap: string | null;
  gapMessage: string | null;
  /** 'shop' = a Shop pay-rate row; 'hourly' = the worker's pay profile. */
  rateSource?: 'shop' | 'hourly' | null;
  workLocation?: 'shop' | 'field' | null;
  /** Non-null only for an hourly shift: the job's state, else the company's. */
  provinceState?: string | null;
  /** The clock record's client, on an hourly FIELD shift only. */
  customerName?: string | null;
}

/** Exhaustive over what the API sends. Anything else is a dash, not a guess. */
const RATE_SOURCE_LABELS: Record<'shop' | 'hourly', string> = {
  hourly: 'Hourly rate',
  shop: 'Shop rate',
};
const WORK_LOCATION_LABELS: Record<'shop' | 'field', string> = {
  field: 'Field',
  shop: 'Shop',
};

/**
 * `workDate` is a calendar day ('YYYY-MM-DD'), not an instant: built locally so
 * it is not read as UTC midnight and shown a day early west of Greenwich.
 */
function workDay(d: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d ?? ''));
  if (!m) return '—';
  return format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), 'MMM d');
}

const label = <K extends string>(map: Record<K, string>, v: unknown): string =>
  typeof v === 'string' && v in map ? map[v as K] : '—';

/**
 * Whether the panel is drawn at all.
 *
 * ONLY when a shift was priced at an hourly rate. Every Canadian tenant's
 * hourly set is empty by construction on the API side (clock pay is US-only),
 * so their `shopEntries` are shop-rate rows only and this returns false — the
 * casing Payroll screen renders exactly what it rendered before api#1097.
 * Shop-only periods already have their own report (Timesheet reports → Shop
 * time), which this does not duplicate.
 */
export function hasHourlyClockPay(entries: unknown): entries is ClockPayEntry[] {
  return Array.isArray(entries) && entries.some((e) => e?.rateSource === 'hourly');
}

/**
 * The clock-in shifts this Generate priced, one row per shift, in the API's
 * figures. Nothing here is computed: the amount and rate are the server's, a
 * shift it could not price shows its reason instead of a number.
 */
export default function ClockPayEntries({ entries, onDismiss }: { entries: ClockPayEntry[]; onDismiss: () => void }) {
  if (!hasHourlyClockPay(entries)) return null;

  return (
    <section data-testid="payroll-clock-pay" className="mx-4 mt-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
      <header className="flex items-start justify-between gap-3 border-b border-fieldiq-border px-4 py-3">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Generation report</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Clock-in pay</h2>
        </div>
        <button onClick={onDismiss} className="text-fieldiq-muted hover:text-white" aria-label="Dismiss clock-in pay">
          <X size={14} />
        </button>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-fieldiq-muted">
              {['Worker', 'Date', 'Location', 'State', 'Customer', 'Rate source', 'Hours', 'Rate', 'Amount'].map((h) => (
                <th key={h} className="px-3 py-2 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.timesheetId} data-testid="payroll-clock-pay-row" className="border-t border-fieldiq-border text-fieldiq-text">
                <td className="px-3 py-2 whitespace-nowrap">{e.workerName || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{workDay(e.workDate)}</td>
                <td className="px-3 py-2">{label(WORK_LOCATION_LABELS, e.workLocation)}</td>
                <td className="px-3 py-2">{e.provinceState || '—'}</td>
                <td className="px-3 py-2">{e.customerName || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{label(RATE_SOURCE_LABELS, e.rateSource)}</td>
                <td className="px-3 py-2 tabular-nums">{fmtHours(e.hours)}</td>
                <td className="px-3 py-2 tabular-nums whitespace-nowrap">{e.hourlyRate === null ? '—' : fmtCurrency(e.hourlyRate, null, 2, ' / hr')}</td>
                <td className="px-3 py-2 tabular-nums">
                  {e.amount === null ? (
                    <span className="text-fieldiq-muted">{shopGapMessage(e.gap, e.gapMessage)}</span>
                  ) : (
                    fmtCurrency(e.amount)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
